import type { SelectChangeEvent } from "@mui/material";
import { MenuItem, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow } from "@mui/material";
import LeftAlignment from "assets/img/svg/leftAlignment";
import type { TImportTitles } from "components/ImportData/import_data";
import type { Dispatch, SetStateAction } from "react";
import { Fragment, useCallback, useState } from "react";
import { useTranslation } from "react-i18next";

import StyledErrorSelect from "../TableErrorDropdown";
import styles from "./style.module.scss";
import { removeLastItemArray } from "./utils";

interface IMappingTable {
    /**
     * Options avaible for mapping.
     */
    options: TImportTitles | undefined;
    /**
     * Columns that need to be matched (either with an option or ignore value.)
     */
    columnsToMatch: string[];
    /**
     * Data from file.
     */
    loadedData: any;
    /**
     * To update fileData with new data (if they change columns)
     */
    setLoadedData: any;
    /**
     * Columns matched.
     */
    selectedValues: Record<string, string>;
    /**
     * Sets columns matched.
     */
    setSelectedValues: Dispatch<SetStateAction<Record<string, string>>>;
    /**
     * Updates number of columns still needing to be matched.
     * Not used in bulk updates as all columns are initially set to Ignore.
     */
    setNotMatched?: Dispatch<SetStateAction<number | null>>;
    /**
     * Array with name of fields (our variables) that are mandatory.
     */
    mandatoryFields?: string[];
    /**
     * Names for the columns (also helps to determine number of columns.)
     */
    columnNames: string[];
    /**
     * Array with keys that can be repeated (like channelId).
     */
    repeatedKeys?: string[];
}

/**
 * Table for mapping columns of a file with our values. Used in import and bulk updates.
 */
const MappingTable = ({
    options,
    columnsToMatch,
    mandatoryFields, // not yet used but might be needed when I implement this table on ImportData
    loadedData,
    setLoadedData,
    selectedValues,
    setSelectedValues,
    setNotMatched,
    columnNames,
    repeatedKeys,
}: IMappingTable) => {
    const [t] = useTranslation();

    const [repeatedKey, setRepeatedKey] = useState("");

    /**
     * Finds our corresponding key in selectedValues (ex: {Prénom: name})
     * and cleans or deletes that key from loadedData.
     * @param oldKey oldKey to be deleted
     */
    const deleteKeyFromData = (oldKey: string) => {
        const newData = loadedData.map((item) => {
            const { [oldKey]: __, ...rest } = item;

            // If value is array, remove last item of array.
            const isValueArray = Array.isArray(item[oldKey]);
            if (isValueArray && item[oldKey].length > 1) {
                return { ...item, [oldKey]: removeLastItemArray(item[oldKey]) };
            }

            // If not array or array with only one element, remove key completely.
            return rest;
        });

        return newData;
    };

    /**
     * Updates selected values to assign a match to column.
     * Updates loadedData with the value corresponding to the match.
     * If Ignore value chosen, removes key (both ours and their title) from loadedData.
     */
    const handleChangeSelect = useCallback(
        (e: SelectChangeEvent<unknown>) => {
            const name = e.target.name as string; // Column name
            const value = e.target.value as string; // Value is either our keys or "Ignore"

            if (!selectedValues[name]) {
                setNotMatched && setNotMatched((prev) => prev! - 1);
            }

            // Checks if column already chosen and can be repeated.
            // If not allowed, doesn't make any changes and displays error message.
            const selectedMapping = Object.values(selectedValues);
            if (
                value !== "Ignore" &&
                selectedMapping.includes(value) &&
                !repeatedKeys?.includes(value.replace("_", ""))
            ) {
                setRepeatedKey(value);
                return;
            } else {
                setRepeatedKey("");
            }

            // If we change the value of an already selected column, first delete that value in loadedData then make new change.
            // This cleans data and ensures there are no mistakes with old values kept.
            if (value !== "Ignore" && selectedValues[name] !== value) {
                const newData = deleteKeyFromData(selectedValues[name]);
                setLoadedData(newData);
            }

            if (value === "Ignore") {
                // If they choose ignore:
                const newData = deleteKeyFromData(selectedValues[name]);
                setLoadedData(newData);
            } else {
                setLoadedData((prevData) =>
                    prevData.map((item, index) => {
                        // Places repeatedKeys inside array.
                        // Technically this is already done in UploadStep, but if they use our export file (for instance) the keys aren't the same for channels (see terms update).
                        if (value !== "_id" && repeatedKeys?.includes(value.replace("_", ""))) {
                            const newValue = prevData[index][name] || "";
                            const oldValue = prevData[index][value];
                            return { ...item, [value]: oldValue ? [oldValue, newValue].flat() : [newValue].flat() };
                        } else {
                            // If not repeated, treat normally.
                            return {
                                ...item,
                                [value]: prevData[index][name] || "",
                            };
                        }
                    }),
                );
            }

            // Matches their columns with one of our keys (or "Ignore").
            setSelectedValues((prev) => ({
                ...prev,
                [name]: value,
            }));
        },
        [selectedValues, setNotMatched, setSelectedValues, loadedData],
    );

    const formatExampleColumn = (value: any) => {
        if (Array.isArray(value)) {
            const cleanedArray = value.filter((item) => item !== "");
            return cleanedArray.length > 0 ? cleanedArray.join(", ") : "--";
        } else {
            return value || "--";
        }
    };

    return (
        <Fragment>
            {repeatedKey && options && (
                <span className={styles["repeated-key-message"]}>
                    {t("BulkUpdates.MappingStep.ColumnExists", { name: options[repeatedKey] })}
                </span>
            )}
            <Paper className={styles["mapping-table-container"]}>
                <TableContainer>
                    <Table stickyHeader>
                        <TableHead>
                            <TableRow>
                                {columnNames.map((column, index) => {
                                    return (
                                        <TableCell className={styles["th-cell"]} key={`header-${index}`}>
                                            <div>
                                                <LeftAlignment />
                                                <span>{column}</span>
                                            </div>
                                        </TableCell>
                                    );
                                })}
                            </TableRow>
                        </TableHead>

                        <TableBody>
                            {columnsToMatch.map((column, index) => {
                                //A match exists between our columns and options (if it exists in selectValue it means it's been auto-mapped)
                                const columnSuccessfullyMapped = Object.keys(selectedValues).find(
                                    (item) => item === column,
                                );

                                return (
                                    <TableRow key={`titleKey-${index}`}>
                                        <TableCell className={styles["first-column"]}>{column}</TableCell>
                                        <TableCell className={styles["matching-column"]}>
                                            <StyledErrorSelect
                                                name={column}
                                                value={selectedValues[column] ?? ""}
                                                onChange={handleChangeSelect}
                                                displayEmpty
                                                className={styles["matching-select"]}
                                                sx={{
                                                    color: !columnSuccessfullyMapped ? "#A16262" : "#000",
                                                    backgroundColor: !columnSuccessfullyMapped
                                                        ? "rgba(197, 83, 83, 0.12)"
                                                        : "#fff",
                                                }}
                                                fullWidth
                                                renderValue={
                                                    !columnSuccessfullyMapped
                                                        ? () => <span>{t("ImportData.NotMatchedColumn")}</span>
                                                        : undefined
                                                }
                                            >
                                                <MenuItem
                                                    key="ignore"
                                                    value="Ignore"
                                                    sx={{ fontFamily: "Poppins, sans-serif", fontStyle: "italic" }}
                                                >
                                                    {t("BulkUpdates.Ignore")}
                                                </MenuItem>

                                                {options &&
                                                    Object.keys(options).map((key, index) => {
                                                        return (
                                                            <MenuItem
                                                                key={`${key}_${index}`}
                                                                value={key}
                                                                sx={{ fontFamily: "Poppins, sans-serif" }}
                                                            >
                                                                {options[key]}
                                                            </MenuItem>
                                                        );
                                                    })}
                                            </StyledErrorSelect>
                                        </TableCell>
                                        <TableCell className={styles["example-column"]} id={`data-${index}`}>
                                            {/** If column mapped, display value in loadedData that matches file column */}
                                            {loadedData.length !== 0 && columnSuccessfullyMapped
                                                ? formatExampleColumn(loadedData[0][column])
                                                : "--"}
                                        </TableCell>
                                    </TableRow>
                                );
                            })}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Paper>
        </Fragment>
    );
};

export default MappingTable;
